import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchExperienceDates, fetchExperienceTimes } from 'actions/ExperienceActions'
import Pane from 'components/Common/Pane'
import CurrencyContext from 'contexts/currencyContext'
import ExperienceTicketSelection from '../ExperienceTickets/ExperienceTicketSelection'
import ExperienceTicketInputs from '../ExperienceTickets/ExperienceTicketInputs'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { ExperienceDetailsForm } from '../ExperienceDetailsModal/ExperienceDetailsBookingModal'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { arrayToObject, min, sum, max } from 'lib/array/arrayUtils'
import cn from 'clsx'
import { generateExperienceCheckoutItem } from 'lib/checkout/experiences/cart'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import ExperiencePageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import ExperiencePageDispatchContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageDispatchContext'
import { ExperienceOfferPageActions } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { arrayToStringListFormat } from 'lib/string/stringUtils'
import { addExperienceToCheckout } from 'checkout/lib/analytics/checkoutUAMappers'
import { addGTMEvent, fireEcommerceGTMEvent } from 'api/googleTagManager'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCartGTM } from 'analytics/eventDefinitions'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { generateExperienceGiftCardItem } from 'lib/checkout/gift/cart'
import { getTotalSeats } from 'lib/experiences/experienceUtils'
import ExperienceTicketCustomerLimit from '../ExperienceTickets/ExperienceTicketCustomerLimit'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import useExperienceAvailableDates from 'hooks/ExperiencePage/useExperienceAvailableDates'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import usePrevious from 'hooks/usePrevious'
import { EXPERIENCE_ANY_DATE } from 'constants/experience'

const TicketContainer = styled(Pane)`
  padding: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
`

const TicketContainerWithoutPadding = styled(TicketContainer)`
  padding: 0;
`

const PaddingVerticalSpacer = styled(VerticalSpacer)`
  padding: ${rem(24)};
`

const FadingPriceRowPriceCaption = styled(PriceRowPriceCaption)`
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const BottomContent = styled.div`
  display: grid;
  grid-template-areas:
    "save . ."
    "price . buy";
  grid-template-columns: auto 1fr auto;
  grid-row-gap: 4px;
  grid-column-gap: 20px;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-areas: "price save buy";
  }
`

const PriceGridItem = styled.div`
  grid-area: price;
`
const SaveGridItem = styled.div`
  grid-area: save;

  ${mediaQueryUp.tablet} {
    text-align: right;
  }
`
const BuyGridItem = styled.div`
  grid-area: buy;
`

interface Props {
  experienceView: App.ExperienceBookingView;
  isBuyNowBookLater?: boolean;
  isGift?: boolean;
}

interface FetchDatesParams {
  pickupId?: string;
  locationId?: string;
}

function ExperienceOfferPageTicketSelection(props: Props) {
  const {
    experienceView,
    isBuyNowBookLater = false,
    isGift = false,
  } = props

  const currency = useContext(CurrencyContext)
  const dispatch = useAppDispatch()

  const trackEvent = useTrackExperienceOfferEvent()

  const handleOptionPurchaseEvent = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackEvent('BNBL_BUY_NOW_CLICKED')
    }
    if (isGift) {
      return trackEvent('GIFT_BUY_NOW_CLICKED')
    }
    return trackEvent('BUY_NOW_CLICKED')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [isBuyNowBookLater, isGift])

  const onTicketCountIncrease = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackEvent('BNBL_INCREASE_OPTION_SELECTED')
    }
    if (isGift) {
      return trackEvent('GIFT_INCREASE_OPTION_SELECTED')
    }
    return trackEvent('INCREASE_OPTION_SELECTED')
  }, [isBuyNowBookLater, isGift]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const onTicketCountDecrease = useCallback(() => {
    if (isBuyNowBookLater) {
      return trackEvent('BNBL_DECREASE_OPTION_SELECTED')
    }
    if (isGift) {
      return trackEvent('GIFT_DECREASE_OPTION_SELECTED')
    }
    return trackEvent('DECREASE_OPTION_SELECTED')
  }, [isBuyNowBookLater, isGift]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const onTicketCountChange = useCallback((increment: boolean) => {
    if (increment) {
      return onTicketCountIncrease()
    }
    return onTicketCountDecrease()
  }, [onTicketCountDecrease, onTicketCountIncrease])

  const { date, time, pickupPoint: selectedPickupPoint, ticketCounts, redemptionLocation: selectedLocation, language: selectedLanguage } = useContext(ExperiencePageStateContext)
  const experienceDispatch = useContext(ExperiencePageDispatchContext)

  const setTicketCounts = useCallback((ticketCounts: Record<string, number>) => experienceDispatch({ type: ExperienceOfferPageActions.setTicketCounts, ticketCounts }), [experienceDispatch])

  const setDate = useCallback((date?: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setDate, date })
  }, [experienceDispatch])

  const setSelectedPickupPoint = useCallback((pickupPoint?: App.ExperiencePickupPoint) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setPickupPoint, pickupPoint })
  }, [experienceDispatch])

  const setTimeSlot = useCallback((time: string) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setTime, time })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [experienceDispatch])

  const setSelectedLocation = useCallback((redemptionLocation?: App.ExperienceRedemptionLocation) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setRedemptionLocation, redemptionLocation })
  }, [experienceDispatch])

  const setLanguage = useCallback((language: App.ExperienceLanguage) => {
    experienceDispatch({ type: ExperienceOfferPageActions.setLanguage, language })
  }, [experienceDispatch])

  const experience = experienceView.originalExperience
  const pickupOptional = experience?.pickupOptional

  const availableDatesState = useExperienceAvailableDates(experienceView.originalExperience, {
    pickupPointId: selectedPickupPoint?.id,
    redemptionLocationId: selectedLocation?.id,
  })

  const availableTimesState = useExperienceAvailableTimes(experienceView.originalExperience, {
    date,
    redemptionLocationId: selectedLocation?.id,
    pickupPointId: selectedPickupPoint?.id,
    isBuyNowBookLater,
    isGift,
  })

  const loading = !!(availableDatesState?.fetching || availableTimesState?.fetching)
  const availableDates = availableDatesState?.dates
  const availableTimes = availableTimesState?.slots

  const previousAvailableDates = usePrevious(availableDates)

  const fetchDates = useCallback(({ pickupId, locationId }: FetchDatesParams) => {
    setDate()
    const pickupPointId = pickupOptional ? undefined : pickupId
    dispatch(fetchExperienceDates(experienceView.id, { pickupPointId, redemptionLocationId: locationId }))
  }, [pickupOptional, dispatch, experienceView.id, setDate])

  const fetchTimes = useCallback((day?: string) => {
    const pickupPointId = pickupOptional ? undefined : selectedPickupPoint?.id
    dispatch(
      fetchExperienceTimes(experienceView.id, {
        date: day,
        pickupPointId,
        redemptionLocationId: selectedLocation?.id,
        isBuyNowBookLater,
        isGift,
      }),
    )
  }, [experienceView.id, selectedPickupPoint?.id, selectedLocation?.id, pickupOptional, dispatch, isBuyNowBookLater, isGift])

  const loadDates = useCallback(() => {
    if (!experience) return

    const pickups = experience.pickupPoints
    const locations = experience.redemptionLocations

    if (isBuyNowBookLater || isGift) {
      fetchTimes(EXPERIENCE_ANY_DATE)
    } else if ((experience.pickupOptional || selectedPickupPoint) && (!locations || locations?.length === 0)) {
      // pickup points aren't required, so we can go straight to fetching dates
      fetchDates({ pickupId: selectedPickupPoint?.id })
    } else if ((pickups?.length <= 1) || locations?.length <= 1) {
      // no or only 1 pickup point, can go directly to fetching dates
      if (!pickups?.length && !locations?.length) {
        fetchDates({})
      }
      else if (pickups?.length === 1) {
        setSelectedPickupPoint(pickups[0])
        fetchDates({ pickupId: pickups[0]?.id })
        trackEvent('PICKUP_LOCATION_SELECTED_NON_INTERACTION')
      } else if (locations?.length === 1) {
        setSelectedLocation(locations[0])
        fetchDates({ locationId: locations[0]?.id })
        trackEvent('REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE')
      }
    }

    if (date) fetchTimes(date)
  }, [experience, isBuyNowBookLater, isGift, selectedPickupPoint, date, fetchTimes, fetchDates, setSelectedPickupPoint, trackEvent, setSelectedLocation])

  useEffect(() => {
    loadDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceView])

  useEffect(() => {
    if (availableDates?.length && !experience.hasCalendar) {
      // we still fetch calendar data even though it doesn't have it as we need it
      // to go fetch the times which gives us the tickets
      const day = isBuyNowBookLater || isGift ? EXPERIENCE_ANY_DATE : availableDates[0].day
      setDate(day)
      fetchTimes(day)
      trackEvent('DATE_ON_CALENDAR_SELECTED_NON_INTERACTION')
    }
  }, [availableDates]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // After login the availableDates and availableTimes gets cleared
    // in the context, this will fire a refetch of dates and times
    // to update the limits for the new customer
    if (previousAvailableDates?.length && !availableDates?.length) {
      loadDates()
    }
  }, [previousAvailableDates, availableDates, loadDates])

  const onPickupPointSelect = useCallback((newPickupPoint?: App.ExperiencePickupPoint) => {
    if (newPickupPoint && selectedPickupPoint && newPickupPoint.id === selectedPickupPoint.id) return

    setSelectedPickupPoint(newPickupPoint)
    fetchDates({ pickupId: newPickupPoint?.id })
  }, [fetchDates, setSelectedPickupPoint, selectedPickupPoint])

  const onLocationSelect = useCallback((newLocation?: App.ExperienceRedemptionLocation) => {
    if (newLocation && selectedLocation && newLocation.id === selectedLocation.id) return

    setSelectedLocation(newLocation)
    fetchDates({ locationId: newLocation?.id })
  }, [fetchDates, setSelectedLocation, selectedLocation])

  const onDateSelect = useCallback((newDate: App.ExperienceAvailabilityDate) => {
    if (date === newDate.day) return

    setDate(newDate.day)
    fetchTimes(newDate.day)
  }, [fetchTimes, setDate, date])

  const handleTicketCountChange = useCallback((ticket: App.ExperienceBookingView['ticketOptions'][number], nextCount: number) => {
    setTicketCounts({
      ...ticketCounts,
      [ticket.id]: nextCount,
    })
    onTicketCountChange((ticketCounts[ticket.id] ?? 0) < nextCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onDecrementClickTicket, onIncrementClickTicket are stable
  }, [setTicketCounts, ticketCounts])

  const allTicketOptions = useExperienceTicketsOptions(availableTimes, { isBuyNowBookLater, isGift, time })

  const counts = useMemo(() => {
    return Object.entries(ticketCounts).map(([ticketId, count]) => {
      return {
        ticket: allTicketOptions.find(t => t.id === ticketId)!,
        count,
      }
      // sometimes the tickets we previously selected are no longer available
      // remove them
    }).filter(count => !!count.ticket)
  }, [ticketCounts, allTicketOptions])

  const totalTicketCount = useMemo(() => sum(counts, count => count.count), [counts])

  const [isFromPrice, totalPrice] = useMemo(() => {
    const cheapestTicket = min(allTicketOptions, (ticket) => ticket.price)
    const ticketsById = arrayToObject(
      allTicketOptions,
      (ticket) => ticket.id,
    )
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(
      ([id, count]) => (ticketsById[id]?.price ?? 0) * count,
    )
    const selectedPrice = sum(selectedTicketPrices)
    return [
      selectedPrice === 0,
      selectedPrice || experienceView.price || cheapestTicket?.price || 0,
    ]
  }, [ticketCounts, experienceView, allTicketOptions])

  const proceedToCheckout = useCallback((ticketData: ExperienceDetailsForm) => {
    if (isGift && experience.giftType === 'gift-card') {
      const item = generateExperienceGiftCardItem(
        experience,
        totalPrice,
        counts,
      )
      dispatch(addToCheckoutCart([item]))
    } else {
      const item = generateExperienceCheckoutItem(
        experience,
        ticketData,
        counts,
        {
          isBuyNowBookLater,
          isGift,
        },
      )

      dispatch(addToCheckoutCart([item], isGift ? { isGift } : undefined))
    }
    Analytics.track('add-to-cart', addToCart())
    dispatch(navigateToCheckout())
  }, [isGift, experience, dispatch, totalPrice, counts, isBuyNowBookLater])

  const handleBuyNow = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleOptionPurchaseEvent()
    const ticketData = formToObject<ExperienceDetailsForm>(e.currentTarget)
    delete ticketData.ticketCount // Unneeded field
    counts.map(ticket => {
      addGTMEvent(addExperienceToCheckout(experienceView, ticket, currency))
    })
    if (isBuyNowBookLater || isGift) {
      proceedToCheckout({
        date: EXPERIENCE_ANY_DATE,
        language: selectedLanguage?.id,
        pickupPoint: selectedPickupPoint?.id,
        redemptionLocation: selectedLocation?.id,
        time: ticketData.time,
      })
    } else {
      proceedToCheckout(ticketData)
    }

    const ticketQuantity = sum(counts ?? [], count => count.count)
    fireEcommerceGTMEvent(addToCartGTM(experience, currency, totalPrice, ticketQuantity, 'experience'))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onBuyNow is stable
  }, [counts, experience, currency, totalPrice, experienceView, isBuyNowBookLater, isGift, proceedToCheckout, selectedLanguage?.id, selectedLocation?.id, selectedPickupPoint?.id])

  const userSelectedFields = useMemo(() => {
    const fields: Array<string> = []
    if (experience.redemptionLocations.length > 1 || experience.pickupPoints.length > 1) {
      fields.push('location')
    }
    if (experience.hasCalendar && !availableDatesState?.error) {
      fields.push('date')

      if (experience.hasTimeslots && !availableTimesState?.error) {
        fields.push('time')
      }
    }
    if (experience.languages.length > 1) {
      fields.push('language')
    }
    return fields
  }, [availableDatesState?.error, availableTimesState?.error, experience])

  const currentTimeSlot = useMemo(() => {
    if (availableTimes) {
      if (isBuyNowBookLater || isGift) {
        let timeslots = availableTimes.filter(slot => slot.purchaseLimit.maxReason !== 'AVAILABILITY')
        if (!timeslots.length) timeslots = availableTimes

        return max<App.ExperienceAvailabilityTimeSlot>(timeslots, (slot => slot.purchaseLimit.max))
      } else {
        return availableTimes.find(slot => slot.time === time)
      }
    }
  }, [availableTimes, time, isBuyNowBookLater, isGift])

  const { totalUsedSeatsOnCart, totalSeatsRemaining } = useMemo(() => {
    if (!currentTimeSlot) return { totalSeatsRemaining: 0, totalUsedSeatsOnCart: 0, totalUsedSeatsOnServer: 0 }
    return getTotalSeats(currentTimeSlot, ticketCounts)
  }, [ticketCounts, currentTimeSlot])

  const hasError = availableDatesState?.error || availableTimesState?.error
  const emptyTimeSlots = availableTimes && !availableTimes.length
  // for voucher based sometimes some tickets or all tickets will be sold out
  const offerWithAllTicketsSoldOut = emptyTimeSlots && !hasError && !loading

  const minSeats = currentTimeSlot?.purchaseLimit.min ?? 1

  const orderMode = useMemo<App.ExperienceOrderMode>(() => {
    switch (true) {
      case isGift:
        return 'gift'
      case isBuyNowBookLater:
        return 'bnbl'
      default:
        return 'booking'
    }
  }, [isGift, isBuyNowBookLater])

  return (<VerticalSpacer as="form" gap={20} onSubmit={handleBuyNow}>
    {!isBuyNowBookLater && !isGift &&
      <TicketContainerWithoutPadding>
        <LoadingIndicator floating visible={loading} />
        <PaddingVerticalSpacer gap={20} >
          <ExperienceTicketInputs
            experienceView={experienceView}
            onTimeSelect={setTimeSlot}
            onPickupPointSelect={onPickupPointSelect}
            onLocationSelect={onLocationSelect}
            allDates={availableDates}
            selectedDate={date ?? ''}
            selectedTimeSlot={time ?? ''}
            defaultLanguageId={selectedLanguage?.id}
            defaultPickupPointId={selectedPickupPoint?.id}
            defaultRedemptionLocationId={selectedLocation?.id}
            availableTimes={availableTimes}
            onDateSelect={onDateSelect}
            onLanguageSelect={setLanguage}
          />
          {(hasError || offerWithAllTicketsSoldOut) && <BodyText variant="large" colour="critical">
            There are no tickets available for your selection
            {userSelectedFields.length > 0 ? `, try another ${arrayToStringListFormat(userSelectedFields)}` : ''}
            .
          </BodyText>}
        </PaddingVerticalSpacer>
        {allTicketOptions.length > 0 && !!currentTimeSlot && <ExperienceTicketCustomerLimit
          purchaseLimit={currentTimeSlot.purchaseLimit}
          totalSeatsRemaining={totalSeatsRemaining}
          totalUsedSeatsOnCart={totalUsedSeatsOnCart}
        />}
      </TicketContainerWithoutPadding>
    }
    <LoadingIndicator floating visible={(isBuyNowBookLater || isGift) && loading} />
    {allTicketOptions.length > 0 && <TicketContainerWithoutPadding>
      <PaddingVerticalSpacer gap={20}>
        <LoadingIndicator floating visible={(!isBuyNowBookLater || !isGift) && loading} />
        <ExperienceTicketSelection
          ticketOptions={allTicketOptions}
          ticketCounts={ticketCounts}
          onTicketCountChange={handleTicketCountChange}
          purchaseLimitRemaining={totalSeatsRemaining}
          purchaseLimitReason={currentTimeSlot?.purchaseLimit.maxReason}
          orderMode={orderMode}
        />
      </PaddingVerticalSpacer>
      {(isBuyNowBookLater || isGift) && !!currentTimeSlot && <ExperienceTicketCustomerLimit
        purchaseLimit={currentTimeSlot.purchaseLimit}
        totalSeatsRemaining={totalSeatsRemaining}
        totalUsedSeatsOnCart={totalUsedSeatsOnCart}
      />}
    </TicketContainerWithoutPadding>}
    <BottomContent>
      <PriceGridItem>
        <FadingPriceRowPriceCaption className={cn({ visible: isFromPrice })}>
          From
        </FadingPriceRowPriceCaption>
        <PriceRowPrice
          size="M"
          price={totalPrice}
          saleUnit={(experience.ticketUnitLabel && isFromPrice) ? experience.ticketUnitLabel : 'total'}
        />
      </PriceGridItem>
      <TripGuard>
        <SaveGridItem>
          <ExperienceBookmarkButton offer={experience} />
        </SaveGridItem>
      </TripGuard>
      <BuyGridItem>
        <TextButton
          kind="primary"
          size="large"
          type="submit"
          disabled={experience.status !== 'ONLINE' || totalTicketCount === 0 || minSeats > totalUsedSeatsOnCart}
        >
          Buy now
        </TextButton>
      </BuyGridItem>
    </BottomContent>
  </VerticalSpacer>
  )
}

export default ExperienceOfferPageTicketSelection
